import { z } from 'zod';
import { PropertySchema, PropertyTagSchema } from './property';
import { BareFeedTemplateSchema } from './feedTemplate';
import { DayType } from './time';
import { BarePlanSchema, PlanSchema } from './plan';
import { objectIdSchema } from './objectId';
import { WithIdSchema, WithTimestampSchema } from './utils';
import { StrategicLibraryTransfer } from './eventTemplate';

export const TemplateSourceType = ['project', 'sailing', 'template'] as const;

export const SourceSchema = z.object({
  id: objectIdSchema,
  type: z.enum(TemplateSourceType),
});

export const BareTemplateBaseSchema = z.object({
  name: z.string(),
  plan: BarePlanSchema,
  feeds: BareFeedTemplateSchema.array().optional(),
  deleted: z.boolean().optional(),
  property: objectIdSchema,
  tags: z.string().array(),
  dayTheme: z.string().optional(),
  source: SourceSchema,
});

export const BareTemplateSchema =
  BareTemplateBaseSchema.merge(WithIdSchema).merge(WithTimestampSchema);

export const SimpleTemplateStatsSchema = z.object({
  dayType: z.enum(DayType),
  eventsCount: z.number(),
  venueDataCount: z.number(),
  contentCount: z.number(),
});

export const BareSimpleTemplateSchema = BareTemplateSchema.omit({
  feeds: true,
  plan: true,
}).merge(SimpleTemplateStatsSchema);

export const TemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  plan: PlanSchema,
  deleted: z.boolean().optional(),
  feeds: BareFeedTemplateSchema.array().optional(),
  property: PropertySchema, 
  tags: PropertyTagSchema.array(), 
  source: SourceSchema,
  dayTheme: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const SimpleTemplateSchema = TemplateSchema.omit({ feeds: true, plan: true }).merge(
  SimpleTemplateStatsSchema
);

export const SailingTemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  deleted: z.boolean().optional(),
  property: PropertySchema,
  type: z.enum(TemplateSourceType),
  sourceId: objectIdSchema,
  dayTemplates: TemplateSchema.array(),
});

export const SimpleSailingTemplateSchema = SailingTemplateSchema.omit({ dayTemplates: true }).merge(
  z.object({
    dayTemplates: SimpleTemplateSchema.array(),
  })
);

export const BareSailingTemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  deleted: z.boolean().optional(),
  property: objectIdSchema,
  type: z.enum(TemplateSourceType),
  sourceId: objectIdSchema,
  dayTemplates: TemplateSchema.array(),
});

export const BuildSailingDayTemplateOptionsSchema = z.object({
  name: z.string().optional(),
  tags: z.string().array().optional(),
});

export const StrategicLibraryTransferSchema = z.enum(StrategicLibraryTransfer);

export const TemplateTransferTaskSchema = z.object({
  templateId: objectIdSchema,
  templateName: z.string(),
  targetProperty: objectIdSchema,
  eventsFix: z.array(
    z.object({
      eventId: objectIdSchema,
      strategy: StrategicLibraryTransferSchema.optional(),
    })
  ),
  contentsFix: z.array(
    z.object({
      contentId: objectIdSchema,
      strategy: StrategicLibraryTransferSchema.optional(),
    })
  ),
  venuesMap: z.array(
    z.object({
      fromId: objectIdSchema,
      toId: z.string().optional(),
    })
  ),
});

export type Template = z.infer<typeof TemplateSchema>;
export type BareSimpleTemplate = z.infer<typeof BareSimpleTemplateSchema>;
export type SimpleSailingTemplate = z.infer<typeof SimpleSailingTemplateSchema>;
export type SimpleTemplate = z.infer<typeof SimpleTemplateSchema>;
export type BareTemplate = z.infer<typeof BareTemplateSchema>;
